import React from 'react';

import { T, FONT_STACK } from '../customer/customers.theme';

/**
 * "Install app" — the floating button, bottom right.
 *
 * The till runs in a browser tab, which means a tab somebody can close by
 * accident, an address bar taking up room, and a shortcut nobody set up.
 * Installing it fixes all three: the app gets its own window, its own icon on
 * the desktop and taskbar, and no browser chrome.
 *
 * ── When it appears ──────────────────────────────────────────────────────
 *
 * Only when the browser says the app can actually be installed. Chrome fires
 * `beforeinstallprompt` once it is satisfied — a manifest, icons, https, and
 * a registered service worker — and that event is the ONLY way to open the
 * install dialog from a button of our own. No event, no button: there is
 * nothing to offer, and a button that did nothing would be worse than none.
 *
 * So it stays hidden when the app is already installed and in a browser that
 * does not support installing (Firefox on the desktop, Safari).
 *
 * ── Closing it is not a decision ─────────────────────────────────────────
 *
 * The ✕ hides it for THIS page load and nothing more. Nothing is written to
 * disk and nothing is remembered — a refresh brings it straight back.
 *
 * That is deliberate. Somebody who waves it away while taking an order has
 * not decided they never want the app installed; they have decided they are
 * busy. Remembering that for a fortnight would quietly take the offer away
 * from the one person most likely to want it, and there is no way back to it
 * from inside the app. A button that returns on the next refresh costs a
 * glance; one that vanishes for two weeks costs a support call.
 *
 * ── Where it sits ────────────────────────────────────────────────────────
 *
 * Bottom right, clear of everything else that lives at the bottom of a
 * screen: the login screen's support numbers and connection dot are bottom
 * LEFT, and the "Profile updated" toast is bottom CENTRE.
 *
 * Its z-index is deliberately between the app bar (1201) and the dialogs
 * (1300+), so it floats above the page but never over a payment dialog or a
 * confirmation somebody is trying to read.
 */

const COPY = {
  install: 'Install app',
  hint: 'Install Dalabtire on this computer',
  dismiss: 'Not now — it comes back when the page reloads',
};

/**
 * Whether the app is already running as an installed app rather than in a
 * tab. Two checks because Safari never adopted the standard one.
 *
 * Every read is wrapped: `matchMedia` is missing in some embedded browsers,
 * and a throw here would take the whole app down for a floating button.
 */
function isInstalled() {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
  } catch (err) {
    /* assume not */
  }
  return false;
}

const DownloadIcon = ({ size = 16, width = 2.2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3.5v11M7 10l5 5 5-5" />
    <path d="M4 19.5h16" />
  </svg>
);

const CloseIcon = ({ size = 13, width = 2.2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeLinecap="round"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export default function InstallButton() {
  const [prompt, setPrompt] = React.useState(null);
  // In memory only. Nothing about this button survives a refresh.
  const [hidden, setHidden] = React.useState(() => isInstalled());
  const [busy, setBusy] = React.useState(false);
  const [hover, setHover] = React.useState(false);

  React.useEffect(() => {
    /**
     * The event has to be captured and kept. Chrome fires it once, and
     * calling `preventDefault` is what stops its own mini-infobar from
     * appearing so this button can be the one that asks.
     */
    const onBeforeInstall = (event) => {
      event.preventDefault();
      setPrompt(event);
    };

    /**
     * Fires however the app was installed — this button, or Chrome's own menu
     * item. Either way there is nothing left to offer.
     */
    const onInstalled = () => {
      setPrompt(null);
      setHidden(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt || busy) return;
    setBusy(true);
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice && choice.outcome === 'dismissed') {
        // They saw the browser's own dialog and closed it. Leaving the button
        // sitting there would be pestering — but only until the next reload,
        // like every other way of dismissing it.
        setHidden(true);
      }
    } catch (err) {
      /* the dialog could not be opened; leave the button where it is */
    } finally {
      // A captured prompt is single-use — Chrome refuses a second call on the
      // same event. Dropping it here means the button cannot be pressed twice
      // into an error.
      setPrompt(null);
      setBusy(false);
    }
  };

  const dismiss = () => setHidden(true);

  if (hidden || !prompt) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 1250,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        fontFamily: FONT_STACK,
      }}
    >
      <button
        type="button"
        title={COPY.hint}
        onClick={install}
        disabled={busy}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          height: 44,
          padding: '0 20px',
          border: 'none',
          borderRadius: T.radiusPill,
          background: hover ? T.brandInk : T.brand,
          color: T.surface,
          font: 'inherit',
          fontFamily: FONT_STACK,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 0.2,
          cursor: busy ? 'progress' : 'pointer',
          boxShadow: '0 6px 20px rgba(21,24,29,0.22)',
          opacity: busy ? 0.75 : 1,
          transition: 'background 120ms ease, opacity 120ms ease',
        }}
      >
        <DownloadIcon />
        {COPY.install}
      </button>

      <button
        type="button"
        aria-label={COPY.dismiss}
        title={COPY.dismiss}
        onClick={dismiss}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          marginLeft: 6,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusPill,
          background: T.surface,
          color: T.inkFaint,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(21,24,29,0.12)',
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
}
